'use client';

import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { isFuture, isToday } from 'date-fns';
import { DateTime } from 'luxon';
import { Fragment, memo, useMemo, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { BasesOuts } from '~/components/BasesOuts';
import { ConditionalLink } from '~/components/Buttons';
import { CaretIcon, ChevronLeft, ChevronRight } from '~/components/Icons';
import { useGameRegistryData } from '~/components/layouts/GameDataProvider';
import { appConfig } from '~/config/app-config';
import franchiseApiData from '~/data/franchiseApiData.json';
import type { Broadcast, IGame, IPlayByPlay, ITeam } from '~/types/api';
import { trackTicketStartCart, type GameTrackingData } from '~/utils/analytics';
import { GAME_STATES, ISSUE_STATUSES } from '~/utils/gameState';
import { getBroadcastIcon } from '~/utils/getBroadcastIcon';
import { getColors } from '~/utils/getColors';
import { getLocalDateTime } from '~/utils/getLocalDateTime';
import { shortName } from '~/utils/shortName';
import { withOrdinal } from '~/utils/withOrdinal';
import { squadUpState } from './SquadUp';

// Extract common styles into constants
const COMMON_STYLES = {
  teamName: 'flex items-center gap-x-[6.75px] uppercase',
  scoreText: 'font-bold',
  liveIndicator: 'flex items-center gap-x-1',
  timerContainer:
    'mb-2 flex h-5 items-center justify-between gap-x-4 text-[12px] font-semibold uppercase',
};

// always show latest season
// TODO: this needs to handle all-star cup and ausl

// Memoize sub-components for better performance
const MemoizedDateSlide = memo(DateSlide);
const MemoizedGameSlide = memo(GameSlide);
const MemoizedLiveGameSlide = memo(LiveGameSlide);

export function ScheduleBar({ team }: { team?: string }) {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [isStart, setIsStart] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  // Replace useState + useEffect with useQuery
  const { data: schedule } = useQuery({
    queryKey: ['schedule', appConfig.ausl.currentSeasonId, team],
    queryFn: async () => {
      const response = await fetch(
        `/api/schedule?seasonId=${appConfig.ausl.currentSeasonId.toString()}`,
        {
          cache: 'no-cache',
        },
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status} - ${response.statusText}`);
      }

      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Memoize all data processing in a single useMemo to maintain hook order
  const processedData = useMemo(() => {
    // Check if we have schedule data
    if (!schedule || !('games' in schedule) || !schedule.games.length) {
      return { filteredGamesData: null, groupedGamesByDate: [] };
    }

    // Filter the schedule to only show today's and future games
    let filteredGames = schedule.games.filter((game: IGame) => {
      if (!game.gameDateIso) return false;

      // Parse the date and convert to a JS Date object
      const gameDate = DateTime.fromISO(game.gameDateIso).toJSDate();

      // Show the game if it's today or in the future
      return isToday(gameDate) || isFuture(gameDate);
    });

    // Filter games by team if a team is provided
    if (team) {
      filteredGames = filteredGames.filter(
        (game: IGame) =>
          game.competitors?.[0]?.name?.toLowerCase() === team ||
          game.competitors?.[1]?.name?.toLowerCase() === team,
      );
    }

    // Sort games by date (earliest first)
    filteredGames = filteredGames.sort((a: IGame, b: IGame) => {
      if (!a.gameDateIso || !b.gameDateIso) return 0;
      return DateTime.fromISO(a.gameDateIso) < DateTime.fromISO(b.gameDateIso)
        ? -1
        : 1;
    });

    // Skip if there are no games to show
    if (!filteredGames.length) {
      return { filteredGamesData: null, groupedGamesByDate: [] };
    }

    const filteredGamesData = {
      games: filteredGames.map((game: any) => ({
        ...game,
        competitors: game.competitors || [],
        homeTeam: game.competitors?.find(
          (c: ITeam) => c.competitorId === game.homeTeamId,
        ),
        awayTeam: game.competitors?.find(
          (c: ITeam) => c.competitorId === game.awayTeamId,
        ),
      })),
    };

    // Group games by date to show date header once per day
    // Create a map to group games by date
    const dateMap = new Map<string, { dateObj: string; games: IGame[] }>();

    // Group games by their formatted date (e.g., "JUL 12")
    filteredGamesData.games.forEach((game: IGame) => {
      if (!game.gameDateIso || !game.competitors) return;

      const date = DateTime.fromISO(game.gameDateIso, { zone: 'UTC' })
        .toLocal()
        .toFormat('LLL dd');

      if (!dateMap.has(date)) {
        dateMap.set(date, {
          dateObj: game.gameDateIso,
          games: [],
        });
      }

      dateMap.get(date)?.games.push(game);
    });

    // Convert map to array sorted by date
    const groupedGamesByDate = Array.from(dateMap.entries()).sort(
      ([_, dataA], [__, dataB]) => {
        return DateTime.fromISO(dataA.dateObj) < DateTime.fromISO(dataB.dateObj)
          ? -1
          : 1;
      },
    );

    return { filteredGamesData, groupedGamesByDate };
  }, [schedule, team]);

  const { filteredGamesData, groupedGamesByDate } = processedData;

  if (!filteredGamesData) return null;

  return (
    <div
      id="schedule-bar"
      className="flex border-[#7a7a7a] bg-black text-white lg:order-first lg:border-b"
    >
      <button
        className={clsx(
          'grid min-w-[30px] place-items-center border-[#7a7a7a] max-lg:hidden lg:border-r',
          isStart ? 'opacity-0' : 'opacity-100',
        )}
        onClick={() => {
          swiper?.slidePrev();
        }}
      >
        <ChevronLeft />
      </button>

      <Swiper
        className="my-4 w-full px-2 py-[12.5px]"
        //
        slidesPerView="auto"
        spaceBetween={8}
        onSwiper={setSwiper}
        onSlideChange={(swiper) => {
          setIsStart(swiper.isBeginning);
          setIsEnd(swiper.isEnd);
        }}
      >
        {groupedGamesByDate.map(([_, { dateObj, games }], groupIndex) => (
          <Fragment key={`group-${groupIndex}`}>
            {/* Date slide - shown only once per day */}
            <SwiperSlide key={`date-${groupIndex}`} className="max-w-max">
              <MemoizedDateSlide date={dateObj} />
            </SwiperSlide>

            {/* Game slides for this date */}
            {games.map((game: IGame, gameIndex) => (
              <SwiperSlide
                key={`game-${groupIndex}-${gameIndex}`}
                className="max-w-max"
              >
                {game.recordStatus === GAME_STATES.IN_PROGRESS ? (
                  <MemoizedLiveGameSlide game={game} />
                ) : (
                  <MemoizedGameSlide game={game} />
                )}
              </SwiperSlide>
            ))}
          </Fragment>
        ))}
      </Swiper>

      <button
        className={clsx(
          'grid min-w-[30px] place-items-center border-[#7a7a7a] max-lg:hidden lg:border-l',
          isEnd ? 'opacity-0' : 'opacity-100',
        )}
        onClick={() => {
          swiper?.slideNext();
        }}
      >
        <ChevronRight />
      </button>
    </div>
  );
}

function DateSlide({ date }: { date: string }) {
  const weekday = DateTime.fromISO(date, { zone: 'UTC' })
    .toLocal()
    .toFormat('EEE', { locale: 'en-US' });
  const month = DateTime.fromISO(date, { zone: 'UTC' })
    .toLocal()
    .toFormat('LLL', { locale: 'en-US' });
  const day = DateTime.fromISO(date, { zone: 'UTC' })
    .toLocal()
    .toFormat('dd', { locale: 'en-US' });

  return (
    <div className="grid h-full min-w-[50px] place-items-center">
      <div className="grid place-items-center gap-y-2 uppercase">
        <div>
          <p className="-mb-[6.5px] -mt-[5.5px] text-[14px]">{weekday}</p>
        </div>
        <div>
          <p className="-mb-[9.5px] -mt-[7.5px] text-[20px] font-bold">
            {month}
          </p>
        </div>
        <div>
          <p className="-mb-[9.5px] -mt-[7.5px] text-[20px] font-bold">{day}</p>
        </div>
      </div>
    </div>
  );
}

function LiveGameSlide({ game }: { game: IGame }) {
  const isLiveGame = game.recordStatus === GAME_STATES.IN_PROGRESS;

  // Use the centralized game data system for live data
  const { data: gameData } = useGameRegistryData(game);

  return <GameSlide game={game} liveGame={gameData?.playByPlayData} />;
}

function GameSlide({
  game,
  liveGame,
}: {
  game: IGame;
  liveGame?: IPlayByPlay | null;
}) {
  // Memoize expensive computations
  const {
    homeTeam,
    awayTeam,
    link,
    isPreGame,
    isLiveGame,
    time,
    state,
    homeRecord,
    awayRecord,
    homeTeamScore,
    awayTeamScore,
  } = useMemo(() => {
    const homeTeam =
      game.competitors?.find(
        (c: ITeam) => c.competitorId === game.homeTeamId,
      ) ?? null;
    const awayTeam =
      game.competitors?.find(
        (c: ITeam) => c.competitorId === game.awayTeamId,
      ) ?? null;
    const link = `/game/${game.gameId}`;
    const isPreGame = game.recordStatus === GAME_STATES.SCHEDULED;
    const isLiveGame = game.recordStatus === GAME_STATES.IN_PROGRESS;

    const localDateTime = game.gameDateIso
      ? getLocalDateTime(game.gameDateIso)
      : null;
    const time = typeof localDateTime === 'object' ? localDateTime?.time : null;

    const state = liveGame?.gameStatus?.gameState;

    const homeRecord =
      franchiseApiData.data.find(
        (franchise) => franchise.teamName === game.homeTeam?.name,
      )?.record ?? '0 – 0';
    const awayRecord =
      franchiseApiData.data.find(
        (franchise) => franchise.teamName === game.awayTeam?.name,
      )?.record ?? '0 – 0';

    // Get latest scores from liveGame data if available, otherwise use game scores
    const homeTeamScore =
      isLiveGame && liveGame?.gameStatus?.homeTeamScore !== undefined
        ? (liveGame.gameStatus.homeTeamScore ?? 0)
        : game.homeTeamScore;

    const awayTeamScore =
      isLiveGame && liveGame?.gameStatus?.awayTeamScore !== undefined
        ? (liveGame.gameStatus.awayTeamScore ?? 0)
        : game.awayTeamScore;

    return {
      homeTeam,
      awayTeam,
      link,
      isPreGame,
      isLiveGame,
      time,
      state,
      homeRecord,
      awayRecord,
      homeTeamScore,
      awayTeamScore,
    };
  }, [game, liveGame]);

  // Memoize the event handlers to prevent unnecessary rerenders
  const handleTicketClick = useMemo(
    () => (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (game.ticketLink?.includes('squadup.com')) {
        e.preventDefault();

        const parts = game.ticketLink.split('/');
        const id = parts[parts.length - 1];

        // Pass event data to squadUpState like in ScheduleGame component
        squadUpState.eventData = {
          gameId: game.gameId,
          homeTeam: game.competitors?.find(
            (c: ITeam) => c.competitorId === game.homeTeamId,
          ),
          awayTeam: game.competitors?.find(
            (c: ITeam) => c.competitorId === game.awayTeamId,
          ),
          date: game.gameDateIso,
          venue: game.venue,
        };

        squadUpState.eventId = id;
      }

      // Track ticket start cart using the shared analytics utility
      trackTicketStartCart(game as unknown as GameTrackingData);
    },
    [game],
  );

  // if (isLiveGame) {
  //   console.log('state', state);
  //   console.log('gameId', game.gameId);
  //   console.log('gameNumber', game.gameNumber);
  //   console.log('outs', state?.outs);
  //   console.log('inning', state?.inning);
  //   console.log('firstBaseRunnerId', state?.firstBaseRunnerId);
  //   console.log('secondBaseRunnerId', state?.secondBaseRunnerId);
  //   console.log('thirdBaseRunnerId', state?.thirdBaseRunnerId);
  // }

  const broadcast = game?.broadcasts?.find(
    (b: Broadcast) => b.broadcastTypeLk === 'BROAD',
  );

  return (
    <ConditionalLink
      href={!isPreGame ? link : undefined}
      className="group relative block min-w-[182px] rounded-sm border border-white/50 p-[11px]"
    >
      <div className={COMMON_STYLES.timerContainer}>
        {isPreGame ? (
          <>
            <p>Upcoming</p>
            <p>{time}</p>
          </>
        ) : isLiveGame ? (
          <>
            <p>Live</p>
            {getBroadcastIcon(broadcast?.name ?? '', 'h-2')}
            <span className={COMMON_STYLES.liveIndicator}>
              {state?.inning ? (
                <CaretIcon
                  className={clsx('w-2', state.topInningFlg && 'rotate-180')}
                />
              ) : (
                ''
              )}
              {state?.inning ? withOrdinal(state.inning) : ''}
            </span>
            <BasesOuts
              bases={
                !!state
                  ? [
                      !!state.thirdBaseRunnerId,
                      !!state.secondBaseRunnerId,
                      !!state.firstBaseRunnerId,
                    ]
                  : [false, false, false]
              }
              outs={state?.outs ?? 0}
              className="h-[19px] w-[17px]"
            />
          </>
        ) : (
          <p
            className={clsx(
              'my-4 rounded-sm px-2 text-sm font-bold uppercase [&_span]:leading-[20px]',
              (ISSUE_STATUSES as readonly string[]).includes(
                game.recordStatus,
              ) && 'bg-[#DB1500]',
            )}
          >
            <span>
              {game.recordStatus === GAME_STATES.NOT_YET_SCHEDULED
                ? 'TBD'
                : game.recordStatus === GAME_STATES.COMPLETED
                  ? 'Final'
                  : game.recordStatus}
            </span>
          </p>
        )}
      </div>

      <div className="mb-3 flex items-center justify-between text-[14px] last:mb-0">
        <p className={COMMON_STYLES.teamName}>
          <span
            className="block h-[23px] w-[23px] rounded-full"
            style={{
              backgroundColor: getColors(awayTeam?.name?.toLowerCase() ?? '')
                ?.primary,
            }}
          />
          {shortName(awayTeam?.name ?? '')}
        </p>

        {isPreGame ? <p>{awayRecord}</p> : <p>{awayTeamScore ?? ''}</p>}
      </div>
      <div className="flex items-center justify-between text-[14px]">
        <p className={COMMON_STYLES.teamName}>
          <span
            className="block h-[23px] w-[23px] rounded-full"
            style={{
              backgroundColor: getColors(homeTeam?.name?.toLowerCase() ?? '')
                ?.primary,
            }}
          />
          {shortName(homeTeam?.name ?? '')}
        </p>

        {isPreGame ? <p>{homeRecord}</p> : <p>{homeTeamScore ?? ''}</p>}
      </div>

      {isPreGame && (
        <div className="absolute inset-0 flex h-full flex-col bg-black text-[14px] font-bold uppercase tracking-[1.12px] opacity-0 group-hover:opacity-100">
          <a
            href={game.ticketLink || link}
            className="flex flex-grow items-center justify-center border-b border-white/50 text-white/50 hover:text-white"
            onClick={handleTicketClick}
          >
            Tickets
          </a>
          <a
            href={link}
            className="flex flex-grow items-center justify-center text-white/50 hover:text-white"
          >
            Details
          </a>
        </div>
      )}
    </ConditionalLink>
  );
}
